import { message, notification, Modal } from 'antd';

/*
let start using VarexeAPIResponseHandler in all the api calls 


*/


/**
 * Configuration: Default behavior for automatic error handling
 * 
 * Set to true: Errors are NOT automatically displayed (components handle manually)
 * Set to false: Errors ARE automatically displayed by the interceptor
 * 
 * TODO: Once all components are updated to use varexeAPIResponseHandler, change this to false
 */
const DEFAULT_SKIP_AUTO_HANDLING = true;

/**
 * Default display type when backend does not send display_type.
 * Allowed values: 'message' | 'notification' | 'modal'
 */
const DEFAULT_DISPLAY_TYPE = 'message';

/**
 * Centralized error handler for API responses
 * Formats and displays errors using antd message, notification, or modal based on display_type from backend
 */
class VarexeAPIResponseHandler {

  /**
   * Display a message using the appropriate antd component based on display_type and message type.
   * For notification/modal the title is derived from type (Error, Success, Warning, Info).
   * @param {string} msg - Message (body) to display
   * @param {string} displayType - 'message' | 'notification' | 'modal' (default: 'message')
   * @param {string} type - 'error' | 'success' | 'warning' | 'info' (default: 'error')
   */
  _displayMessage(msg, displayType = DEFAULT_DISPLAY_TYPE, type = 'error') {
    const normalizedDisplayType = (displayType && ['message', 'notification', 'modal'].includes(displayType))
      ? displayType
      : DEFAULT_DISPLAY_TYPE;
    const normalizedMsgType = ['error', 'success', 'warning', 'info'].includes(type) ? type : 'error';
    const title = normalizedMsgType.charAt(0).toUpperCase() + normalizedMsgType.slice(1);

    if (normalizedDisplayType === 'notification') {
      notification[normalizedMsgType]({
        // message: title,
        description: msg,
        duration: 10,
        placement: 'top',
        showProgress: true,
      });
      return;
    }
    if (normalizedDisplayType === 'modal') {
      Modal[normalizedMsgType]({
        // title,
        content: msg,
      });
      return;
    }
    message[normalizedMsgType](msg, 5);
  }

  _displayError(errorMessage, displayType = DEFAULT_DISPLAY_TYPE) {
    this._displayMessage(errorMessage, displayType, 'error');
  }



  /**
   * Laravel MessageBag is usually `{ "items.0.field": ["msg"] }` on `response.data.errors`.
   * Some callers wrap again as `{ errors: { ... } }` — support both.
   */
  _getValidationErrorMap(errorData) {
    const top = errorData?.errors;
    if (!top || typeof top !== 'object' || Array.isArray(top)) {
      return null;
    }
    if (top.errors && typeof top.errors === 'object' && !Array.isArray(top.errors)) {
      return top.errors;
    }
    return top;
  }

  formatValidationErrors(errorData) {
    const validationErrors = this._getValidationErrorMap(errorData);
    if (!validationErrors || Object.keys(validationErrors).length === 0) {
      return errorData?.message || 'Validation failed';
    }

    const errorMessages = [];

    Object.entries(validationErrors).forEach(([field, errors]) => {
      if (field === 'message' || field === 'success') {
        return;
      }
      if (Array.isArray(errors)) {
        errors.forEach(error => {
          // Format: "Field: error message" or just "error message"
          const fieldLabel = this.formatFieldName(field);
          errorMessages.push(`${fieldLabel}: ${error}`);
        });
      } else if (typeof errors === 'string') {
        const fieldLabel = this.formatFieldName(field);
        errorMessages.push(`${fieldLabel}: ${errors}`);
      }
    });

    // Join all errors with line breaks or return single message
    if (errorMessages.length === 0) {
      return errorData?.message || 'Validation failed';
    }

    // If only one error, return it directly
    if (errorMessages.length === 1) {
      return errorMessages[0];
    }

    // Multiple errors - format as a readable list
    // Use semicolon separator for better readability in message component
    return errorMessages.join('; ');
  }

  formatFieldName(field) {
    if (typeof field !== 'string') {
      return 'Field';
    }
    const configurableGroupMatch = field.match(/^configurables\.(\d+)\.(.+)$/);
    if (configurableGroupMatch) {
      const optionNum = parseInt(configurableGroupMatch[1], 10) + 1;
      const sub = configurableGroupMatch[2].replace(/_/g, ' ');
      return `Option ${optionNum} (${sub})`;
    }
    // e.g. items.0.configurable_groups.1.configurables.2.quantity
    const itemsMatch = field.match(/^items\.(\d+)\.?(.*)$/);
    if (itemsMatch) {
      const lineNum = parseInt(itemsMatch[1], 10) + 1;
      const rest = (itemsMatch[2] || '').trim();
      if (!rest) {
        return `Line ${lineNum}`;
      }
      const segments = rest.split('.').filter(Boolean);
      const pretty = segments.map((seg, i) => {
        if (/^\d+$/.test(seg)) {
          const n = parseInt(seg, 10) + 1;
          const key = segments[i - 1] || '';
          if (key === 'configurable_groups') {
            return `group ${n}`;
          }
          if (key === 'configurables') {
            return `option ${n}`;
          }
          return `#${n}`;
        }
        if (seg === 'configurable_groups') return 'configuration groups';
        if (seg === 'configurables') return 'options';
        if (seg === 'quantity') return 'quantity';
        if (seg === 'item_configurable_id') return 'option id';
        return seg.replace(/_/g, ' ');
      }).join(' → ');
      return `Line ${lineNum} (${pretty})`;
    }
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Handle API error response
   * @param {Object} error - Error object from axios
   * @param {Object} options - Options for error handling
   * @param {boolean} options.skipAutoHandling - Skip automatic error display
   * @returns {Object} - Formatted error object
   */
  handleError(error, options = {}) {
    const { skipAutoHandling = DEFAULT_SKIP_AUTO_HANDLING } = options;

    // Network/connection errors (no response data, use default display_type)
    if (!error.response) {
      if (!skipAutoHandling) {
        this._displayError('Network Error: Please check your internet connection.', DEFAULT_DISPLAY_TYPE);
      }
      return {
        message: 'Network Error: Please check your internet connection.',
        type: 'network',
        display_type: DEFAULT_DISPLAY_TYPE,
        originalError: error
      };
    }

    const { response } = error;
    const { status, data } = response;

    // Handle specific status codes
    // Always show errors for license and not_found regardless of skipAutoHandling
    const displayType = data?.display_type || DEFAULT_DISPLAY_TYPE;

    if (status === 403) {
      const errorMessage = data?.message || 'License Required';
      // Always display license errors
      this._displayError(errorMessage, displayType);
      return {
        message: errorMessage,
        type: 'license',
        status,
        display_type: displayType,
        data
      };
    }

    if (status === 404) {
      const errorMessage = data?.message || 'Page not Found';
      // Always display 404 errors
      this._displayError(errorMessage, displayType);
      return {
        message: errorMessage,
        type: 'not_found',
        status,
        display_type: displayType,
        data
      };
    }

    // Handle validation errors (422, 400)
    if (status === 422 || status === 400) {
      const errorMessage = this.formatValidationErrors(data);
      const validationDisplayType = data?.display_type || DEFAULT_DISPLAY_TYPE;

      if (!skipAutoHandling) {
        this._displayError(errorMessage, validationDisplayType);
      }

      return {
        message: errorMessage,
        type: 'validation',
        status,
        display_type: validationDisplayType,
        data,
        errors: data?.errors
      };
    }

    // Handle unauthorized (401)
    // Always show errors for unauthorized unless redirect is provided (redirect takes precedence)
    if (status === 401) {
      const errorMessage = data?.message || 'Unauthorized access';
      const unauthorizedDisplayType = data?.display_type || DEFAULT_DISPLAY_TYPE;

      // Check if response contains a redirect URL
      if (data?.redirect) {
        // If redirect is provided, don't show error message (redirect will happen)
        return {
          message: errorMessage,
          type: 'unauthorized',
          status,
          display_type: unauthorizedDisplayType,
          data,
          redirect: data.redirect
        };
      }

      // Always display unauthorized errors (unless redirect is provided)
      this._displayError(errorMessage, unauthorizedDisplayType);

      return {
        message: errorMessage,
        type: 'unauthorized',
        status,
        display_type: unauthorizedDisplayType,
        data
      };
    }

    // Handle other errors
    const errorMessage = data?.message || `Error: ${status} - Something went wrong.`;
    const errorDisplayType = data?.display_type || DEFAULT_DISPLAY_TYPE;

    if (!skipAutoHandling) {
      this._displayError(errorMessage, errorDisplayType);
    }

    return {
      message: errorMessage,
      type: 'error',
      status,
      display_type: errorDisplayType,
      data
    };
  }


  success(msgOrData) {
    const { msg, displayType } = this._normalizeMessageArgs(msgOrData);
    this._displayMessage(msg, displayType, 'success');
  }

  error(msgOrData) {
    const { msg, displayType } = this._normalizeMessageArgs(msgOrData);
    this._displayMessage(msg, displayType, 'error');
  }

  warning(msgOrData) {
    const { msg, displayType } = this._normalizeMessageArgs(msgOrData);
    this._displayMessage(msg, displayType, 'warning');
  }

  info(msgOrData) {
    const { msg, displayType } = this._normalizeMessageArgs(msgOrData);
    this._displayMessage(msg, displayType, 'info');
  }

  _normalizeMessageArgs(msgOrData) {
    if (msgOrData && typeof msgOrData === 'object' && typeof msgOrData.message === 'string') {
      return {
        msg: msgOrData.message,
        displayType: msgOrData.display_type || DEFAULT_DISPLAY_TYPE,
      };
    }
    return {
      msg: typeof msgOrData === 'string' ? msgOrData : 'Unknown',
      displayType: DEFAULT_DISPLAY_TYPE,
    };
  }
}

// Export singleton instance and display type constant for consumers
export { DEFAULT_DISPLAY_TYPE };
export default new VarexeAPIResponseHandler();

